import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type DeployMode = 'local' | 'cloud';

interface ConfigState {
  /** Per-deployment mode. Comes from the runtime config.json — never baked into the bundle. */
  mode: DeployMode;
  /** Cloud mode only: the site the user is currently addressing (`/sites/{id}`). null until picked. */
  siteId: string | null;
  /** Display name of the selected site (cached alongside the id so the shell can show it offline). */
  siteName: string | null;
  /** True once the runtime config has been resolved (config.json fetched or fallen back). */
  loaded: boolean;
  /** ⚠ True once config.json has been read SUCCESSFULLY at least once on this console.
   *  Persisted, so a later boot whose network fails can tell "I know what tier I am" from
   *  "I have never been told" — the difference between keeping a session and mis-shaping
   *  every URL in the app. */
  resolvedOnce: boolean;
}

interface ConfigActions {
  applyMode: (mode: DeployMode, resolved?: boolean) => void;
  /** Select the cloud-mode site (id used for `/sites/{id}` routing, name for display). */
  setSite: (id: string, name: string | null) => void;
  /** Clear the selection (returns to the site picker). */
  clearSite: () => void;
}

export const useConfigStore = create<ConfigState & ConfigActions>()(
  persist(
    (set) => ({
      mode: 'local',
      siteId: null,
      siteName: null,
      loaded: false,
      resolvedOnce: false,

      applyMode: (mode, resolved = false) =>
        set((s) => ({ mode, loaded: true, resolvedOnce: s.resolvedOnce || resolved })),
      setSite: (siteId, siteName) => set({ siteId, siteName }),
      clearSite: () => set({ siteId: null, siteName: null }),
    }),
    {
      name: 'xantory:config',
      // The selected site is a user convenience across reloads. `mode` is persisted only as a
      // LAST-KNOWN-GOOD fallback for `loadRuntimeConfig` — config.json still wins whenever it can
      // be read, so a deployment that changes tier is still authoritative on the next boot. What
      // this removes is the silent wrong-mode boot described there.
      partialize: (s) => ({
        siteId: s.siteId,
        siteName: s.siteName,
        mode: s.mode,
        resolvedOnce: s.resolvedOnce,
      }),
    },
  ),
);

interface RuntimeConfig {
  mode?: DeployMode;
}

/**
 * Resolve the per-deployment runtime config BEFORE first render.
 *
 * The deployment (Caddy entrypoint — R-020) serves `/config.json` = `{ "mode": "local" | "cloud" }`.
 * In dev there is no container serving it, so the caller passes a `fallback` (from `VITE_DEPLOY_MODE`).
 * The mode is never compiled into the bundle, so one build runs in either tier (D-037/D-042).
 */
export async function loadRuntimeConfig(fallback: DeployMode = 'local'): Promise<void> {
  // ⚠⚠ **BOOTING IN THE WRONG MODE IS WORSE THAN NOT BOOTING.** `mode` decides how every path is
  // shaped (`resolvePath`): in cloud mode the refresh door is `/users/auth/refresh`, in local mode
  // it is `/auth/refresh`. A cloud console that thinks it is local asks for paths the deployment's
  // proxy does not route, so they fall through to the SPA handler and answer 405 — which used to
  // be read as "your credential was refused" and signed the operator out.
  //
  // That is not hypothetical, and it is exactly a DEPLOY: this is a PWA whose service worker serves
  // the cached shell and swaps the bundle in place (`autoUpdate`, polled every 60s), so the app can
  // boot perfectly while the container serving `/config.json` is still restarting. The fetch is
  // `no-store`, so it MUST reach the network — and it is the one request guaranteed to fail in that
  // window. Silently falling back to the compile-time default then mis-shaped every URL in the app.
  //
  // So: retry briefly, and when the network genuinely cannot answer, prefer the mode this console
  // last resolved successfully over a hardcoded guess. config.json still wins whenever it can be
  // read, which keeps it authoritative for a deployment that changes tier.
  const lastKnownGood = useConfigStore.getState().mode;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch('/config.json', { cache: 'no-store' });
      if (res.ok) {
        const cfg = (await res.json()) as RuntimeConfig;
        if (cfg.mode === 'local' || cfg.mode === 'cloud') {
          useConfigStore.getState().applyMode(cfg.mode, true);
          return;
        }
      }
    } catch {
      /* transport failure — the deploy window, or a genuinely absent config.json in dev */
    }
    // Short, bounded backoff: a container restart is seconds, and first paint waits on this.
    if (attempt < 2) await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
  }

  // ⚠ Never silently assume a tier. `resolvedOnce` separates "this console has read config.json
  // here before and knows what it is" from a genuine first boot, where the caller's fallback
  // (VITE_DEPLOY_MODE in dev) is the only thing there is.
  const { resolvedOnce } = useConfigStore.getState();
  useConfigStore.getState().applyMode(resolvedOnce ? lastKnownGood : fallback);
}
